import React, { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { showAlert } from "store/reducers/alertReducer"

const COPIED_TOOLTIP_MS = 700

const tooltipStyle = {
  position: "absolute",
  left: "50%",
  bottom: "100%",
  transform: "translateX(-50%)",
  marginBottom: 6,
  padding: "5px 10px",
  borderRadius: 6,
  background: "rgba(52, 58, 64, 0.92)",
  color: "#f8f9fa",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  pointerEvents: "none",
  zIndex: 5,
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.18)",
  backdropFilter: "blur(2px)",
}

const tooltipArrowStyle = {
  position: "absolute",
  left: "50%",
  top: "100%",
  transform: "translateX(-50%)",
  width: 0,
  height: 0,
  borderLeft: "5px solid transparent",
  borderRight: "5px solid transparent",
  borderTop: "5px solid rgba(52, 58, 64, 0.92)",
}

/**
 * Order No cell: the number and the copy icon both copy invoiceNo
 * and show a brief "Copied" tooltip.
 */
const OrderNoCopyCell = ({ orderNumber }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [copied, setCopied] = useState(false)
  const hideTimerRef = useRef(null)

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    }
  }, [])

  const copyOrderNumber = async event => {
    event.preventDefault()
    event.stopPropagation()
    const value = orderNumber != null ? String(orderNumber).trim() : ""
    if (!value) return

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value)
      } else {
        const textarea = document.createElement("textarea")
        textarea.value = value
        textarea.setAttribute("readonly", "")
        textarea.style.position = "absolute"
        textarea.style.left = "-9999px"
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand("copy")
        document.body.removeChild(textarea)
      }

      setCopied(true)
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
      hideTimerRef.current = setTimeout(() => {
        setCopied(false)
        hideTimerRef.current = null
      }, COPIED_TOOLTIP_MS)
    } catch (_err) {
      dispatch(showAlert({ text: t("Failed to copy"), type: "failed" }))
    }
  }

  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        whiteSpace: "nowrap",
      }}
    >
      {copied ? (
        <span style={tooltipStyle} role="status" aria-live="polite">
          {t("Copied!")}
          <span style={tooltipArrowStyle} aria-hidden="true" />
        </span>
      ) : null}
      <span
        role="button"
        tabIndex={0}
        className="text-decoration-underline"
        style={{ cursor: "pointer" }}
        title={t("Click to copy")}
        onClick={copyOrderNumber}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            copyOrderNumber(e)
          }
        }}
      >
        {orderNumber}
      </span>
      {orderNumber ? (
        <button
          type="button"
          className={`${
            copied ? "text-success" : "text-primary"
          } cursor-pointer border-0 bg-transparent p-0 d-inline-flex align-items-center`}
          style={{ lineHeight: 1 }}
          title={t("Click to copy")}
          aria-label={t("Copy")}
          onClick={copyOrderNumber}
        >
          <i
            className={`bx ${copied ? "bx-check" : "bx-copy"} font-size-16`}
            aria-hidden="true"
          />
        </button>
      ) : null}
    </span>
  )
}

export default OrderNoCopyCell
